import { useCallback, useEffect, useState } from "react";
import { History, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { PageHeader, Spinner, Tag, EmptyState } from "@/components/common";

const TONE = { executed: "emerald", undone: "cyan", failed: "red", undo_failed: "red", blocked: "amber", validated: "muted", rejected: "muted", execution_on: "amber", execution_off: "muted" };
const AUTO = ["auto", "watchdog"];

const detail = (c) => {
  const d = c.details || {};
  if (d.reasons) return d.reasons.join(" · ");
  if (d.error) return d.error;
  if (d.message) return d.message;
  if (d.operations != null) return `${d.operations} operations`;
  return "";
};

const Filters = ({ f, setF, campaigns }) => (
  <div className="mb-4 flex flex-wrap items-end gap-3" data-testid="changes-filters">
    <select data-testid="changes-filter-actor" className="cc-input w-40 text-sm" value={f.actor} onChange={(e) => setF({ ...f, actor: e.target.value })}>
      <option value="">Auto + Manual</option><option value="auto">Auto only</option><option value="manual">Manual only</option>
    </select>
    <select data-testid="changes-filter-campaign" className="cc-input w-72 text-sm" value={f.campaign} onChange={(e) => setF({ ...f, campaign: e.target.value })}>
      <option value="">All campaigns</option>{campaigns.map((c) => <option key={c}>{c}</option>)}
    </select>
    <label className="text-xs text-slate-500">From<input data-testid="changes-filter-from" type="date" className="cc-input mt-1 text-sm" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} /></label>
    <label className="text-xs text-slate-500">To<input data-testid="changes-filter-to" type="date" className="cc-input mt-1 text-sm" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} /></label>
  </div>
);

export default function Changes() {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ actor: "", campaign: "", date_from: "", date_to: "" });
  const load = useCallback(() => api.get("/changes", { params: f }).then((r) => setData(r.data)), [f]);
  useEffect(() => { load(); }, [load]);
  const undo = async (c) => {
    if (!window.confirm(`Undo in Google Ads?\n\n${c.title}`)) return;
    try { await api.post(`/proposals/${c.proposal_id}/undo`); toast.success("Undone"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return <Spinner />;
  return (
    <div data-testid="changes-page">
      <PageHeader eyebrow="Audit trail" title="Change log" description="Every dry run, block, execution and undo. Auto = Autopilot or the spend watchdog. Undo reverses executed changes in Google Ads." />
      <Filters f={f} setF={setF} campaigns={data.campaigns} />
      {data.changes.length === 0 ? <EmptyState icon={History} title="No changes" description="Nothing matches these filters." testid="changes-empty" /> : (
        <ol className="space-y-2" data-testid="changes-list">{data.changes.map((c, i) => (
          <li key={`${c.at}-${i}`} data-testid={`change-${i}`} className="cc-card flex flex-wrap items-start gap-4 p-4">
            <div className="w-36 font-mono text-[11px] text-slate-500">{fmtDate(c.at)}</div>
            <div className="flex gap-1.5"><Tag tone={AUTO.includes(c.actor) ? "amber" : "muted"} testid={`change-actor-${i}`}>{AUTO.includes(c.actor) ? `Auto${c.actor === "watchdog" ? " · watchdog" : ""}` : "Manual"}</Tag><Tag tone={TONE[c.action] || "muted"}>{c.action.replace("_", " ")}</Tag>{c.risk && <Tag>{c.risk} risk</Tag>}</div>
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-200">{c.title}</div>
              {c.campaigns?.length > 0 && <div className="mt-0.5 text-[11px] text-cyan-300">{c.campaigns.join(" · ")}</div>}
              <div className="mt-0.5 break-words text-xs text-slate-500">{detail(c)}</div>
              {c.reasoning && <div className="mt-1 text-xs italic text-slate-400" data-testid={`change-reasoning-${i}`}>{c.reasoning}</div>}
            </div>
            {c.undoable && <button data-testid={`change-undo-${i}`} onClick={() => undo(c)} className="cc-btn cc-btn-ghost px-3 py-1 text-xs"><Undo2 className="h-3.5 w-3.5" /> Undo</button>}
          </li>
        ))}</ol>
      )}
    </div>
  );
}
